import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaUserPlus, FaEdit, FaTrash, FaEye, FaSort, FaFilter, FaTimes, FaFileDownload, FaFileUpload, FaUser, FaEnvelope, FaUserCheck } from 'react-icons/fa';
import { db, auth } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where, orderBy, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { GoogleMap, Marker, Autocomplete, useJsApiLoader } from '@react-google-maps/api';

const mapLibraries = ['places', 'marker'];

const Parents = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [userId, setUserId] = useState(null);
  const [parents, setParents] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sortField, setSortField] = useState('firstName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    search: ''
  });
  const [isFiltering, setIsFiltering] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    parentId: '',
    email: '',
    phoneNumber: '',
    location: { lat: null, lng: null },
    placeName: '',
  });

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  const [expandedParentId, setExpandedParentId] = useState(null);
  const [studentsByParent, setStudentsByParent] = useState({});
  const [loadingStudents, setLoadingStudents] = useState(false);

  const [, setMapCenter] = useState({ lat: 0, lng: 0 });
  const autocompleteRef = useRef();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { isLoaded: isMapsLoaded, loadError: mapsLoadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: mapLibraries
  });

  const checkAuthAndRole = useCallback(async () => {
    const user = auth.currentUser;
    
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        navigate('/dashboard');
        return;
      }
      
      const userData = userDoc.data();
      setUserRole(userData.role);
      setUserId(user.uid);
      
      if (userData.role === 'parent') {
        const parentQuery = query(
          collection(db, 'parents'),
          where('userId', '==', user.uid)
        );
        const parentSnapshot = await getDocs(parentQuery);
        if (!parentSnapshot.empty) {
          const parentData = parentSnapshot.docs[0].data();
          setParents([{ id: parentSnapshot.docs[0].id, ...parentData }]);
        }
      }
    } catch (err) {
      console.error('Error checking user role:', err);
      navigate('/login');
    }
  }, [navigate]);

  const fetchParents = useCallback(async () => {
    try {
      setIsFiltering(true);
      setError('');
      let q = collection(db, 'parents');
      
      // Apply sorting
      q = query(q, orderBy(sortField, sortDirection));

      const querySnapshot = await getDocs(q);
      let parentsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        parentsList = parentsList.filter(parent => 
          (parent.firstName || '').toLowerCase().includes(searchLower) ||
          (parent.lastName || '').toLowerCase().includes(searchLower) ||
          (parent.parentId || '').toLowerCase().includes(searchLower) ||
          (parent.email || '').toLowerCase().includes(searchLower) ||
          (parent.phoneNumber || '').toLowerCase().includes(searchLower)
        );
      }

      setParents(parentsList);
    } catch (err) {
      console.error('Error fetching parents:', err);
      setError('Failed to fetch parents. Please try again.');
    } finally {
      setIsFiltering(false);
    }
  }, [sortField, sortDirection, filters]);

  useEffect(() => {
    checkAuthAndRole();
  }, [checkAuthAndRole]);

  useEffect(() => {
    if (userRole) {
      fetchParents();
    }
  }, [userRole, fetchParents]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Add a small delay to prevent too many rapid filter changes
    setTimeout(() => {
      fetchParents();
    }, 300);
  };

  const clearFilters = () => {
    setFilters({
      search: ''
    });
  };

  const handleViewParent = (parent) => {
    setSelectedParent(parent);
    setShowViewModal(true);
  };

  const handleEditParent = (parent) => {
    setSelectedParent(parent);
    setFormData({
      ...parent,
      location: parent.location || { lat: null, lng: null },
    });
    setMapCenter(parent.location && parent.location.lat && parent.location.lng ? parent.location : { lat: 0, lng: 0 });
    setShowEditModal(true);
  };

  const handleUpdateParent = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const parentRef = doc(db, 'parents', selectedParent.id);
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        parentId: formData.parentId,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        location: formData.location,
        updatedAt: new Date().toISOString()
      };
      await updateDoc(parentRef, updateData);
      setSuccess('Parent updated successfully!');
      setShowEditModal(false);
      fetchParents();
    } catch (err) {
      console.error('Error updating parent:', err);
      setError(err.message || 'Failed to update parent');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteParent = async (parentId) => {
    if (window.confirm('Are you sure you want to delete this parent?')) {
      try {
        await deleteDoc(doc(db, 'parents', parentId));
        setSuccess('Parent deleted successfully');
        fetchParents();
      } catch (err) {
        console.error('Error deleting parent:', err);
        setError('Failed to delete parent');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Check if parent already exists
      const parentQuery = query(
        collection(db, 'parents'),
        where('parentId', '==', formData.parentId)
      );
      const querySnapshot = await getDocs(parentQuery);

      if (!querySnapshot.empty) {
        setError('A parent with this ID already exists');
        setLoading(false);
        return;
      }

      const parentData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        parentId: formData.parentId,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        location: formData.location,
        placeName: formData.placeName,
        activationStatus: 'pending',
        activationToken: null,
        activationExpiresAt: null,
        invitedBy: null,
        invitedAt: null,
        userId: null,
        lastActivatedAt: null,
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'parents'), parentData);
      setSuccess('Parent added successfully!');
      setShowAddForm(false);
      setFormData({
        firstName: '',
        lastName: '',
        parentId: '',
        email: '',
        phoneNumber: '',
        location: { lat: null, lng: null },
      });
      fetchParents();
    } catch (err) {
      console.error('Error adding parent:', err);
      setError(err.message || 'Failed to add parent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'firstName',
      'lastName',
      'parentId',
      'email',
      'phoneNumber'
    ];
    
    const csv = Papa.unparse({
      fields: headers,
      data: []
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'parents_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setImportFile(file);
      setImportError('');
    } else {
      setImportError('Please select a valid CSV file');
      setImportFile(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setImportError('Please select a file to import');
      return;
    }

    setImportProgress(0);
    setImportError('');
    setImportSuccess('');
    setLoading(true);

    try {
      const text = await importFile.text();
      const { data, errors } = Papa.parse(text, {
        header: true,
        skipEmptyLines: true
      });

      if (errors.length > 0) {
        setImportError('Error parsing CSV file: ' + errors[0].message);
        return;
      }

      const validationErrors = [];
      const validParents = [];

      data.forEach((row, index) => {
        if (!row.firstName || !row.lastName || !row.parentId || !row.email || !row.phoneNumber) {
          validationErrors.push(`Row ${index + 2}: Missing required fields`);
        } else {
          validParents.push({
            firstName: row.firstName,
            lastName: row.lastName,
            parentId: row.parentId,
            email: row.email,
            phoneNumber: row.phoneNumber,
            activationStatus: 'pending',
            activationToken: null,
            activationExpiresAt: null,
            invitedBy: null,
            invitedAt: null,
            userId: null,
            lastActivatedAt: null,
            createdAt: new Date().toISOString()
          });
        }
      });

      if (validationErrors.length > 0) {
        setImportError('Validation errors:\n' + validationErrors.join('\n'));
        return;
      }

      // Upload valid parents to Firebase
      const batchSize = 50;
      const totalBatches = Math.ceil(validParents.length / batchSize);

      for (let i = 0; i < totalBatches; i++) {
        const batch = validParents.slice(i * batchSize, (i + 1) * batchSize);
        await Promise.all(batch.map(parent => addDoc(collection(db, 'parents'), parent)));
        setImportProgress(((i + 1) / totalBatches) * 100);
      }

      setImportSuccess(`Successfully imported ${validParents.length} parents`);
      setShowImportModal(false);
      fetchParents();
    } catch (err) {
      console.error('Error importing parents:', err);
      setImportError('Failed to import parents: ' + err.message);
    } finally {
      setLoading(false);
      setImportFile(null);
    }
  };

  // Fetch students for a given parentId
  const fetchStudentsForParent = async (parentId) => {
    setLoadingStudents(true);
    try {
      const q = query(collection(db, 'students'), where('parentId', '==', parentId));
      const querySnapshot = await getDocs(q);
      const students = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStudentsByParent(prev => ({ ...prev, [parentId]: students }));
    } catch (err) {
      setStudentsByParent(prev => ({ ...prev, [parentId]: [] }));
    } finally {
      setLoadingStudents(false);
    }
  };

  // Handle expanding/collapsing a parent row
  const handleExpandParent = (parent) => {
    if (expandedParentId === parent.id) {
      setExpandedParentId(null);
    } else {
      setExpandedParentId(parent.id);
      if (!studentsByParent[parent.parentId]) {
        fetchStudentsForParent(parent.parentId);
      }
    }
  };

  const hasLinkedStudents = async (parent) => {
    if (studentsByParent[parent.parentId]) {
      return studentsByParent[parent.parentId].length > 0;
    }

    const studentsQuery = query(collection(db, 'students'), where('parentId', '==', parent.parentId));
    const snapshot = await getDocs(studentsQuery);
    return !snapshot.empty;
  };

  const handleActivateParent = async (parent) => {
    if (!(userRole === 'administrator' || userRole === 'manager')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      if (!parent.email || !parent.parentId) {
        setError('Parent must have both email and parent ID before activation.');
        return;
      }

      const linkedStudents = await hasLinkedStudents(parent);
      if (!linkedStudents) {
        setError('Parent activation requires at least one linked student.');
        return;
      }

      setLoading(true);
      const response = await fetch('/api/send-parent-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          parentDocId: parent.id,
          parentId: parent.parentId,
          email: parent.email,
          firstName: parent.firstName,
          lastName: parent.lastName,
          invitedBy: auth.currentUser?.uid || null,
          appBaseUrl: window.location.origin
        })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to send parent activation invite.');
      }

      setSuccess(`Activation invite sent to ${parent.email}`);
      await fetchParents();
    } catch (err) {
      setError(err.message || 'Could not activate parent.');
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(parents.length / pageSize);
  const paginatedParents = parents.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (!userRole) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Parents...</h2>
          <p className="text-gray-500 mt-2">Please wait while we fetch your parent data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-black">Parents Management</h1>
          <div className="flex space-x-4">
            <button
              onClick={downloadTemplate}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              <FaFileDownload />
              <span>Download Template</span>
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              <FaFileUpload />
              <span>Import CSV</span>
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              <FaFilter />
              <span>Filters</span>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors"
            >
              <FaUserPlus />
              <span>Add New Parent</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search parents..."
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                />
              </div>
              <div className="col-span-4 flex justify-end items-center space-x-4">
                {isFiltering && (
                  <div className="text-sm text-gray-500">
                    <span className="animate-pulse">Filtering...</span>
                  </div>
                )}
                <button
                  onClick={clearFilters}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                >
                  <FaTimes />
                  <span>Clear Filters</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Parents List Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-700 font-medium">Showing {paginatedParents.length} of {parents.length} parents</div>
          <div className="flex items-center space-x-2">
            <label htmlFor="pageSize" className="text-sm text-gray-700">Rows per page:</label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] bg-white"
              style={{ minWidth: '80px' }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Parents List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('firstName')}>
                  <div className="flex items-center space-x-1">
                    <span>Parent</span>
                    <FaSort className={`text-gray-400 ${sortField === 'firstName' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('parentId')}>
                  <div className="flex items-center space-x-1">
                    <span>Parent ID</span>
                    <FaSort className={`text-gray-400 ${sortField === 'parentId' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('email')}>
                  <div className="flex items-center space-x-1">
                    <span>Email</span>
                    <FaSort className={`text-gray-400 ${sortField === 'email' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('phoneNumber')}>
                  <div className="flex items-center space-x-1">
                    <span>Phone Number</span>
                    <FaSort className={`text-gray-400 ${sortField === 'phoneNumber' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Location Status</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedParents.map((parent) => (
                <React.Fragment key={parent.id}>
                  <tr className="cursor-pointer hover:bg-gray-50" onClick={() => handleExpandParent(parent)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {parent.firstName} {parent.lastName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parent.parentId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parent.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parent.phoneNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center justify-center">
                        {parent.location && parent.location.lat && parent.location.lng ? (
                          <div className="w-4 h-4 rounded-full bg-green-500" title="Location set"></div>
                        ) : (
                          <div className="w-4 h-4 rounded-full bg-red-500" title="No location set"></div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          parent.activationStatus === 'active'
                            ? 'bg-green-100 text-green-700'
                            : parent.activationStatus === 'invited'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {(parent.activationStatus || 'pending').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-3">
                        <button
                          onClick={e => { e.stopPropagation(); handleViewParent(parent); }}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); alert('Message parent feature coming soon!'); }}
                          className="text-teal-600 hover:text-teal-900"
                          title="Message Parent"
                        >
                          <FaEnvelope />
                        </button>
                        {(userRole === 'administrator' || userRole === 'manager' || (userRole === 'parent' && parent.userId === userId)) && (
                          <>
                            {(userRole === 'administrator' || userRole === 'manager') && (
                              <button
                                onClick={e => { e.stopPropagation(); handleActivateParent(parent); }}
                                className={`${
                                  parent.activationStatus === 'active'
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-green-600 hover:text-green-900'
                                }`}
                                title={parent.activationStatus === 'active' ? 'Parent already active' : 'Activate parent'}
                                disabled={parent.activationStatus === 'active'}
                              >
                                <FaUserCheck />
                              </button>
                            )}
                            <button
                              onClick={e => { e.stopPropagation(); handleEditParent(parent); }}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit Parent"
                            >
                              <FaEdit />
                            </button>
                            {(userRole === 'administrator' || userRole === 'manager') && (
                              <button
                                onClick={e => { e.stopPropagation(); handleDeleteParent(parent.id); }}
                                className="text-red-600 hover:text-red-900"
                                title="Delete Parent"
                              >
                                <FaTrash />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Dropdown for students */}
                  {expandedParentId === parent.id && (
                    <tr>
                      <td colSpan={7} className="bg-gray-50">
                        <div className="px-6 py-4">
                          {loadingStudents ? (
                            <div className="text-gray-500">Loading students...</div>
                          ) : (
                            <div>
                              <div className="font-semibold mb-2 text-[#00B8B8]">Children</div>
                              {studentsByParent[parent.parentId] && studentsByParent[parent.parentId].length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                  {studentsByParent[parent.parentId].map(student => (
                                    <div key={student.id} className="flex items-center space-x-4 bg-white rounded-lg shadow p-4 border border-gray-100">
                                      <div className="flex-shrink-0">
                                        <FaUser className="text-[#00B8B8] text-2xl" />
                                      </div>
                                      <div>
                                        <div className="font-semibold text-gray-800">{student.firstName} {student.lastName}</div>
                                        <div className="text-xs text-gray-500">ID: {student.studentId}</div>
                                        <div className="text-xs text-gray-500">Grade: {student.currentGrade}</div>
                                        <div className="text-xs text-gray-500">School: {student.currentSchool}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-gray-500">No students found for this parent.</div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-end items-center mt-4 space-x-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Next
          </button>
        </div>

        {/* View Parent Modal */}
        {showViewModal && selectedParent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">Parent Details</h2>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-[#00E0E0] mb-4">Personal Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedParent.firstName} {selectedParent.lastName}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Parent ID</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedParent.parentId}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedParent.email}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedParent.phoneNumber}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#00E0E0] mb-4">Home Location</h3>
                    {selectedParent.location && selectedParent.location.lat && selectedParent.location.lng ? (
                      mapsLoadError ? (
                        <div className="text-gray-500 italic">Unable to load map</div>
                      ) : !isMapsLoaded ? (
                        <div className="text-gray-500 italic">Loading map...</div>
                      ) : (
                        <div className="relative">
                          <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '300px' }}
                            center={selectedParent.location}
                            zoom={15}
                            options={{
                              disableDefaultUI: false,
                              zoomControl: true,
                              mapTypeControl: true,
                              streetViewControl: true,
                              fullscreenControl: true,
                              scrollwheel: false,
                              draggable: false
                            }}
                          >
                            <Marker position={selectedParent.location} />
                          </GoogleMap>
                          <div className="mt-2 text-xs text-gray-500">
                            {selectedParent.placeName && (
                              <div className="mb-1">{selectedParent.placeName}</div>
                            )}
                            <div>Lat: {selectedParent.location.lat}, Lng: {selectedParent.location.lng}</div>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="text-gray-500 italic">No location data available</div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Parent Modal */}
        {(showAddForm || showEditModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">
                    {showAddForm ? 'Add New Parent' : 'Edit Parent'}
                  </h2>
                  <button
                    onClick={() => showAddForm ? setShowAddForm(false) : setShowEditModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={showAddForm ? handleSubmit : handleUpdateParent}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left: Form fields */}
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-[#00E0E0]">Parent Information</h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Parent ID *</label>
                        <input
                          type="text"
                          name="parentId"
                          required
                          value={formData.parentId}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          required
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>
                    </div>
                    {/* Right: Map and search box */}
                    <div className="flex flex-col h-full">
                      <div className="flex flex-col h-full">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Home Location *</label>
                        {mapsLoadError ? (
                          <div className="text-red-600 text-sm">Unable to load map tools.</div>
                        ) : !isMapsLoaded ? (
                          <div className="text-gray-500 text-sm">Loading map tools...</div>
                        ) : (
                          <>
                            <Autocomplete
                              onLoad={ref => (autocompleteRef.current = ref)}
                              onPlaceChanged={() => {
                                const place = autocompleteRef.current.getPlace();
                                if (place && place.geometry && place.geometry.location) {
                                  const lat = place.geometry.location.lat();
                                  const lng = place.geometry.location.lng();
                                  setFormData(prev => ({
                                    ...prev,
                                    location: { lat, lng },
                                    placeName: place.formatted_address || place.name || '',
                                  }));
                                  setMapCenter({ lat, lng });
                                }
                              }}
                            >
                              <input
                                type="text"
                                placeholder="Search for address"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                              />
                            </Autocomplete>
                            <GoogleMap
                              mapContainerStyle={{ width: '100%', height: '300px', marginTop: '10px' }}
                              center={formData.location.lat && formData.location.lng ? formData.location : { lat: 0, lng: 0 }}
                              zoom={formData.location.lat && formData.location.lng ? 15 : 2}
                              onClick={e => {
                                const lat = e.latLng.lat();
                                const lng = e.latLng.lng();
                                // Get address from coordinates using reverse geocoding
                                const geocoder = new window.google.maps.Geocoder();
                                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                                  if (status === 'OK' && results[0]) {
                                    setFormData(prev => ({
                                      ...prev,
                                      location: { lat, lng },
                                      placeName: results[0].formatted_address,
                                    }));
                                  } else {
                                    setFormData(prev => ({
                                      ...prev,
                                      location: { lat, lng },
                                      placeName: '',
                                    }));
                                  }
                                });
                                setMapCenter({ lat, lng });
                              }}
                            >
                              {formData.location.lat && (
                                <Marker position={formData.location} />
                              )}
                            </GoogleMap>
                          </>
                        )}
                        {formData.location.lat && (
                          <div className="mt-2 text-xs text-gray-500">
                            {formData.placeName && (
                              <div className="mb-1">{formData.placeName}</div>
                            )}
                            <div>Lat: {formData.location.lat}, Lng: {formData.location.lng}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-4 mt-6">
                    <button
                      type="button"
                      onClick={() => showAddForm ? setShowAddForm(false) : setShowEditModal(false)}
                      className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`flex items-center space-x-2 px-6 py-3 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {showAddForm ? <FaUserPlus /> : <FaEdit />}
                      <span>{loading ? (showAddForm ? 'Adding...' : 'Updating...') : (showAddForm ? 'Add Parent' : 'Update Parent')}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">Import Parents</h2>
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-gray-600 mb-4">
                      Upload a CSV file containing parent information. Make sure your file matches the template format.
                    </p>
                    <button
                      onClick={downloadTemplate}
                      className="text-[#00E0E0] hover:text-[#00B8B8] font-medium"
                    >
                      Download Template
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select CSV File
                    </label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>

                  {importError && (
                    <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg">
                      {importError}
                    </div>
                  )}

                  {importSuccess && (
                    <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg">
                      {importSuccess}
                    </div>
                  )}

                  {importProgress > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-[#00E0E0] h-2.5 rounded-full"
                        style={{ width: `${importProgress}%` }}
                      ></div>
                    </div>
                  )}

                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={() => setShowImportModal(false)}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleImport}
                      disabled={!importFile || loading}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors ${
                        (!importFile || loading) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <FaFileUpload />
                      <span>{loading ? 'Importing...' : 'Import'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Parents; 